import React from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { Calendar } from 'lucide-react';
import es from 'date-fns/locale/es';
import "react-datepicker/dist/react-datepicker.css";

// Registrar el locale español
registerLocale('es', es);

const styles = {
  container: {
    position: 'relative',
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '0.875rem 1.25rem',
    paddingLeft: '3rem',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid var(--border)',
    backgroundColor: 'var(--bg-tertiary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    fontWeight: '500',
    color: 'var(--text)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  inputFocus: {
    borderColor: 'var(--gold)',
    backgroundColor: 'var(--bg-secondary)',
    boxShadow: '0 0 0 4px var(--gold-subtle)',
  },
  icon: {
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--gold)',
    pointerEvents: 'none',
    transition: 'color 0.2s ease',
  },
  iconFocus: {
    color: 'var(--gold-dark)',
  },
};

// Custom styles for the date picker popup
const customStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker {
    font-family: var(--font-body);
    background: var(--bg-elevated);
    border: none;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-2xl);
    padding: 0;
    overflow: hidden;
  }
  
  /* Mobile responsive styles */
  @media (max-width: 768px) {
    .react-datepicker {
      width: 100% !important;
      max-width: 100vw !important;
      left: 0 !important;
      right: 0 !important;
      margin: 0 !important;
      border-radius: 0 !important;
      position: fixed !important;
      bottom: 0 !important;
      top: auto !important;
      transform: none !important;
    }
    
    .react-datepicker__month-container {
      width: 100% !important;
    }
    
    .react-datepicker__month {
      margin: var(--space-3);
    }
    
    .react-datepicker__header {
      padding: var(--space-4) var(--space-3);
    }
    
    .react-datepicker__day-name,
    .react-datepicker__day {
      width: calc((100vw - var(--space-6) * 2) / 7);
      height: 48px;
      line-height: 48px;
      margin: 2px;
    }
    
    .react-datepicker-popper {
      position: fixed !important;
      inset: 0 !important;
      transform: none !important;
      background: rgba(0, 0, 0, 0.5) !important;
      display: flex !important;
      align-items: flex-end !important;
      z-index: 9999 !important;
    }
  }

  .react-datepicker__header {
    background: var(--gradient-gold);
    border-bottom: none;
    border-radius: 0;
    padding: 1rem 0.5rem;
    margin: 0;
  }

  .react-datepicker__current-month {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: 700;
    color: white;
    letter-spacing: var(--tracking-wide);
    margin-bottom: 0.5rem;
    text-transform: capitalize;
  }

  .react-datepicker__day-names {
    display: flex;
    justify-content: space-around;
  }

  .react-datepicker__day-name {
    color: white;
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: var(--tracking-widest);
    text-transform: uppercase;
    width: 2.5rem;
    line-height: 2rem;
  }

  .react-datepicker__month {
    margin: 0.5rem;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-around;
  }

  .react-datepicker__day {
    width: 2.5rem;
    height: 2.5rem;
    line-height: 2.5rem;
    margin: 0.125rem;
    border-radius: var(--radius-lg);
    color: var(--text);
    font-weight: 500;
    transition: all 0.2s ease;
    border: none;
  }

  .react-datepicker__day:hover {
    background: var(--gold-subtle);
    color: var(--gold-dark);
    transform: scale(1.1);
  }

  .react-datepicker__day--selected {
    background: var(--gradient-gold) !important;
    color: white !important;
    font-weight: 600;
    box-shadow: var(--shadow-gold);
  }

  .react-datepicker__day--keyboard-selected {
    background: var(--gold-subtle);
    color: var(--gold-dark);
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: var(--gold);
    position: relative;
  }

  .react-datepicker__day--today::after {
    content: '';
    position: absolute;
    bottom: 0.25rem;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background: var(--gold);
    border-radius: 50%;
  }

  .react-datepicker__day--outside-month {
    color: var(--text-muted);
    opacity: 0.5;
  }
  
  .react-datepicker__month-container {
    float: none;
  }

  .react-datepicker__day--disabled {
    color: var(--text-muted);
    opacity: 0.3;
    cursor: not-allowed;
  }

  .react-datepicker__day--disabled:hover {
    background: transparent;
    transform: none;
  }

  .react-datepicker__navigation {
    top: 1.25rem;
    width: 2rem;
    height: 2rem;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-md);
    transition: all 0.2s ease;
  }

  .react-datepicker__navigation:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
    border-width: 2px 2px 0 0;
    width: 8px;
    height: 8px;
    top: 8px;
  }

  .react-datepicker__navigation--previous {
    left: 1rem;
  }

  .react-datepicker__navigation--next {
    right: 1rem;
  }

  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow {
    border-color: white;
    border-width: 2px 2px 0 0;
  }

  .react-datepicker__year-dropdown,
  .react-datepicker__month-dropdown {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
  }

  .react-datepicker__year-option,
  .react-datepicker__month-option {
    padding: 0.5rem;
    color: var(--text);
    transition: all 0.2s ease;
  }

  .react-datepicker__year-option:hover,
  .react-datepicker__month-option:hover {
    background: var(--gold-subtle);
    color: var(--gold-dark);
  }

  .react-datepicker__year-option--selected,
  .react-datepicker__month-option--selected {
    background: var(--gradient-gold);
    color: white;
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker-popper {
    z-index: 9999 !important;
  }
  
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::after {
    border-bottom-color: var(--gold) !important;
  }
  
  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle::before,
  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle::after {
    border-top-color: var(--gold) !important;
  }

  .react-datepicker__input-container input {
    width: 100%;
  }
`;

export function DatePicker({
  value,
  onChange,
  placeholder = "Seleccionar fecha",
  minDate,
  maxDate,
  disabled = false,
  dateFormat = "yyyy-MM-dd",
  showTimeSelect = false,
  timeFormat = "HH:mm",
  timeIntervals = 15,
  ...props
}) {
  const [focused, setFocused] = React.useState(false);

  // Convert string date to Date object for the picker
  const selectedDate = value ? new Date(value + 'T00:00:00') : null;

  // Handle date change and format it as YYYY-MM-DD string
  const handleDateChange = (date) => {
    if (date) {
      // Format date as YYYY-MM-DD in local time to avoid timezone shifts
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      onChange(formattedDate);
    } else {
      onChange('');
    }
  };

  return (
    <>
      <style>{customStyles}</style>
      <div style={styles.container}>
        <div style={styles.inputWrapper}>
          <Calendar 
            size={20} 
            style={{
              ...styles.icon,
              ...(focused ? styles.iconFocus : {})
            }} 
          />
          <ReactDatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholderText={placeholder}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={dateFormat}
            showTimeSelect={showTimeSelect}
            timeFormat={timeFormat}
            timeIntervals={timeIntervals}
            locale="es"
            popperPlacement="bottom-start"
            customInput={
              <input
                style={{
                  ...styles.input,
                  ...(focused ? styles.inputFocus : {})
                }}
                readOnly
              />
            }
            {...props}
          />
        </div>
      </div>
    </>
  );
}

export default DatePicker;